import {
	Avatar,
	Badge,
	Button,
	Card,
	Flex,
	Group,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Select,
} from '@mantine/core'
import {
	IconBook2,
	IconBriefcase,
	IconCopy,
	IconEye,
	IconEyeOff,
	IconMapPin,
	IconRefresh,
	IconSearch,
	IconTrash,
	IconUsers,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
	FindMyTutorCmsType,
	deleteFindMyTutorCmsItem,
	getFindMyTutorCmsCountries,
	getFindMyTutorCmsList,
	getFindMyTutorCmsStatistics,
	hideFindMyTutorCmsItem,
} from 'src/apis/findMyTutorAPI'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import UserIdentityChip from 'src/components/UserIdentityChip'
import FindMyTutorPanelHeader from './FindMyTutorPanelHeader'
import styles from './TutorPage.module.scss'

interface TutorTableOwner {
	id: string
	name: string
	avatar: string
}

interface TutorTableRow {
	id: string
	no: number
	image: string
	owner?: TutorTableOwner
	name: string
	about: string
	languages: string
	type: 'Tutor' | 'Learner' | 'Post' | 'Course'
	country: string
	location: string
	listDate: string
	updatedDate: string
	isHidden: boolean
	isOnline: boolean
}

interface TutorStats {
	tutors: number
	learners: number
	courses: number
	posts: number
}

interface TutorCountryOption {
	label: string
	value: string
}

interface TutorPageProps {
	type: FindMyTutorCmsType
}

const SORT_OPTIONS = [
	{ label: 'Last updated', value: 'last-updated' },
	{ label: 'Oldest', value: 'oldest' },
	{ label: 'Newest', value: 'newest' },
]

const TYPE_LABELS: Record<FindMyTutorCmsType, TutorTableRow['type']> = {
	tutor: 'Tutor',
	learner: 'Learner',
	course: 'Course',
	post: 'Post',
}

const TYPE_DESCRIPTIONS: Record<FindMyTutorCmsType, string> = {
	tutor: 'Manage tutor profiles shown in Find My Tutor discovery.',
	learner: 'Review learner demand and language-learning profiles.',
	course: 'Moderate tutor-created courses available to learners.',
	post: 'Moderate learner-created posts looking for tutors.',
}

const formatDate = (value?: string) => {
	if (!value) {
		return ''
	}

	const parsed = dayjs(value)
	if (!parsed.isValid()) {
		return ''
	}

	return parsed.format('DD/MM/YYYY HH:mm')
}

const TooltipText = ({
	value,
	lineClamp = 1,
	size = 'sm',
	color = '#344054',
	fw,
}: {
	value?: string
	lineClamp?: number
	size?: string
	color?: string
	fw?: number
}) => {
	const text = value || '-'

	return (
		<CustomTooltip label={text}>
			<Text size={size} color={color} fw={fw} lineClamp={lineClamp}>
				{text}
			</Text>
		</CustomTooltip>
	)
}

const getRowsAndCount = (response: any) => {
	const objects = response?.data?.results?.objects
	const rows = Array.isArray(objects?.rows) ? objects.rows : []
	const count = Number(objects?.count ?? response?.data?.pagination?.total ?? rows.length ?? 0)

	return {
		rows,
		count,
	}
}

const getStats = (response: any): TutorStats => {
	const stats = response?.data?.results?.object || {}

	return {
		tutors: Number(stats?.tutors || 0),
		learners: Number(stats?.learners || 0),
		courses: Number(stats?.courses || 0),
		posts: Number(stats?.posts || 0),
	}
}

const getTypeBadgeColor = (type: TutorTableRow['type']) => {
	if (type === 'Post') {
		return { background: '#f4e9ff', text: '#7d3eb9' }
	}

	if (type === 'Course') {
		return { background: '#e7f7e7', text: '#2f8f49' }
	}

	if (type === 'Learner') {
		return { background: '#eef3ff', text: '#2d5fb8' }
	}

	return { background: '#ede7ff', text: '#5a37b9' }
}

const getBooleanValue = (value: unknown) => {
	if (typeof value === 'boolean') {
		return value
	}

	if (typeof value === 'string') {
		if (value === 'true') {
			return true
		}

		if (value === 'false') {
			return false
		}
	}

	return undefined
}

const resolveIsHidden = (item: any) => {
	const isHidden = getBooleanValue(item?.is_hidden ?? item?.isHidden)
	if (typeof isHidden === 'boolean') {
		return isHidden
	}

	const status = getBooleanValue(item?.status)
	if (typeof status === 'boolean') {
		return !status
	}

	return false
}

const normalizeRow = (
	item: any,
	type: FindMyTutorCmsType,
	index: number,
	pagination: MRT_PaginationState,
): TutorTableRow => {
	return {
		id: String(item?.id || ''),
		no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
		image: String(item?.image || ''),
		owner:
			item?.owner && typeof item.owner === 'object'
				? {
						id: String(item.owner.id || ''),
						name: String(item.owner.name || ''),
						avatar: String(item.owner.avatar || ''),
				  }
				: undefined,
		name: item?.name || '-',
		about: item?.about || '',
		languages: item?.languages || '',
		type: item?.type || TYPE_LABELS[type],
		country: item?.country || '',
		location: item?.location || '',
		listDate: formatDate(item?.created_at),
		updatedDate: formatDate(item?.updated_at),
		isHidden: resolveIsHidden(item),
		isOnline: Boolean(item?.is_online ?? item?.isOnline ?? item?.online),
	}
}

function TutorPage({ type }: TutorPageProps) {
	const [urlSearchParams] = useSearchParams()
	const [rows, setRows] = useState<TutorTableRow[]>([])
	const [stats, setStats] = useState<TutorStats>({
		tutors: 0,
		learners: 0,
		courses: 0,
		posts: 0,
	})
	const [loading, setLoading] = useState(false)
	const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
	const [searchKey, setSearchKey] = useState('')
	const [countrySearch, setCountrySearch] = useState(() =>
		type === 'tutor' ? urlSearchParams.get('country') || '' : '',
	)
	const [countryOptions, setCountryOptions] = useState<TutorCountryOption[]>([])
	const [countryOptionsLoading, setCountryOptionsLoading] = useState(false)
	const [sortValue, setSortValue] = useState<string>('newest')
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [total, setTotal] = useState(0)

	const fetchStats = useCallback(async () => {
		const res = await getFindMyTutorCmsStatistics()
		setStats(getStats(res))
	}, [])

	const fetchCountryOptions = useCallback(async () => {
		if (type !== 'tutor') {
			setCountryOptions([])
			return
		}

		setCountryOptionsLoading(true)

		try {
			const response = await getFindMyTutorCmsCountries({
				page: 1,
				limit: 300,
			})
			const { rows: countryRows } = getRowsAndCount(response)

			setCountryOptions(
				countryRows
					.map((item: any) => {
						const country = String(item?.country || '').trim()
						const amount = Number(item?.amount || 0)

						return country
							? {
									label: `${country} (${amount})`,
									value: country,
							  }
							: null
					})
					.filter(Boolean) as TutorCountryOption[],
			)
		} finally {
			setCountryOptionsLoading(false)
		}
	}, [type])

	const fetchList = useCallback(async () => {
		setLoading(true)

		try {
			const res = await getFindMyTutorCmsList(type, {
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				q: searchKey || undefined,
				country: type === 'tutor' ? countrySearch || undefined : undefined,
				sort: sortValue,
			})
			const { rows: apiRows, count } = getRowsAndCount(res)

			setRows(apiRows.map((item: any, index: number) => normalizeRow(item, type, index, pagination)))
			setTotal(count)
		} finally {
			setLoading(false)
		}
	}, [countrySearch, pagination, searchKey, sortValue, type])

	const refreshPage = useCallback(async () => {
		await Promise.all([fetchStats(), fetchList()])
	}, [fetchList, fetchStats])

	useEffect(() => {
		refreshPage()
	}, [refreshPage])

	useEffect(() => {
		fetchCountryOptions()
	}, [fetchCountryOptions])

	const resolvedCountryOptions = useMemo(() => {
		if (
			!countrySearch ||
			countryOptions.some((option) => option.value === countrySearch)
		) {
			return countryOptions
		}

		return [
			{ label: countrySearch, value: countrySearch },
			...countryOptions,
		]
	}, [countryOptions, countrySearch])

	const handleChangePage = useCallback((value: number) => {
		setPagination((prev) => ({
			...prev,
			pageIndex: value,
		}))
	}, [])

	const handleChangePageSize = useCallback((value: number) => {
		setPagination({
			pageIndex: 1,
			pageSize: value,
		})
	}, [])

	const resetPage = useCallback(() => {
		setPagination((prev) => ({
			...prev,
			pageIndex: 1,
		}))
	}, [])

	const handleSearchQueryInit = useCallback((value: string) => {
		setSearchKey(value || '')
	}, [])

	const handleFilterQueryInit = useCallback((query: Record<string, string>) => {
		const querySort = query.sort
		if (SORT_OPTIONS.some((option) => option.value === querySort)) {
			setSortValue(querySort)
		}

		if (type === 'tutor') {
			setCountrySearch(query.country || '')
		}
	}, [type])

	const handleHide = useCallback(async (row: TutorTableRow) => {
		setActionLoadingId(row.id)

		try {
			await hideFindMyTutorCmsItem(type, row.id, !row.isHidden)
			await refreshPage()
		} finally {
			setActionLoadingId(null)
		}
	}, [refreshPage, type])

	const handleDelete = useCallback(async (row: TutorTableRow) => {
		if (!row.id || (type !== 'course' && type !== 'post')) {
			return
		}

		const typeLabel = type === 'course' ? 'course' : 'post'
		if (!window.confirm(`Delete this ${typeLabel}?`)) {
			return
		}

		setActionLoadingId(row.id)

		try {
			await deleteFindMyTutorCmsItem(type, row.id)
			await refreshPage()
			toast.success(`${TYPE_LABELS[type]} deleted`)
		} finally {
			setActionLoadingId(null)
		}
	}, [refreshPage, type])

	const handleCopyId = useCallback(async (id: string) => {
		if (!id || !navigator?.clipboard?.writeText) {
			return
		}

		await navigator.clipboard.writeText(id)
		toast.success('Copied ID')
	}, [])

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'no',
				header: 'STT',
				size: 48,
				maxSize: 48,
			},
			{
				accessorKey: 'image',
				header: 'Image',
				size: 64,
				maxSize: 64,
				Cell: ({ row }: any) => (
					<Avatar
						src={row.original.image || undefined}
						alt={row.original.name || 'Image'}
						radius="md"
						size={44}
						color="violet"
						className={styles.tableImage}
					>
						{String(row.original.name || '?')
							.trim()
							.charAt(0)
							.toUpperCase() || '?'}
					</Avatar>
				),
			},
			{
				accessorKey: 'name',
				header: 'Name',
				size: 180,
				maxSize: 180,
				Cell: ({ row }: any) => (
					<Stack spacing={2} className={styles.nameCell}>
						<TooltipText value={row.original.name} fw={700} />
						<Group spacing={4} noWrap className={styles.idRow}>
							<div className={styles.idValue}>
								<Text
									size="xs"
									color="dimmed"
									lineClamp={1}
									className={styles.idText}
									title={row.original.id || '-'}
								>
									{row.original.id || '-'}
								</Text>
							</div>
							<button
								type="button"
								onClick={() => handleCopyId(row.original.id)}
								className={styles.copyIdBtn}
								title="Copy ID"
							>
								<IconCopy size={13} />
							</button>
						</Group>
					</Stack>
				),
			},
			...(type === 'course' || type === 'post'
				? [
						{
							accessorKey: 'owner',
							header: 'Owner',
							size: 145,
							maxSize: 145,
							Cell: ({ row }: any) =>
								row.original.owner ? (
									<UserIdentityChip
										id={row.original.owner.id}
										name={row.original.owner.name}
										avatar={row.original.owner.avatar}
										compact
									/>
								) : (
									<Text size="sm" color="dimmed">
										-
									</Text>
								),
						},
				  ]
				: []),
			{
				accessorKey: 'about',
				header: 'About',
				size: 150,
				maxSize: 150,
				Cell: ({ row }: any) => (
					<TooltipText value={row.original.about} lineClamp={2} />
				),
			},
			{
				accessorKey: 'languages',
				header: 'Languages',
				size: 110,
				maxSize: 110,
				Cell: ({ row }: any) => (
					<TooltipText value={row.original.languages} lineClamp={2} />
				),
			},
			{
				accessorKey: 'type',
				header: 'Type',
				size: 82,
				maxSize: 82,
				Cell: ({ row }: any) => {
					const badgeColor = getTypeBadgeColor(row.original.type)
					return (
						<Badge
							radius="xl"
							variant="light"
							sx={{
								textTransform: 'none',
								background: badgeColor.background,
								color: badgeColor.text,
							}}
						>
							{row.original.type}
						</Badge>
					)
				},
			},
			...(type === 'tutor'
				? [
						{
							accessorKey: 'country',
							header: 'Country',
							size: 120,
							maxSize: 120,
							Cell: ({ row }: any) => (
								<TooltipText value={row.original.country} />
							),
						},
				  ]
				: []),
			{
				accessorKey: 'location',
				header: 'Location',
				size: 165,
				maxSize: 165,
				Cell: ({ row }: any) => {
					const shouldHideOnlineLocation =
						(row.original.type === 'Course' || row.original.type === 'Post') &&
						row.original.isOnline

					return !shouldHideOnlineLocation && row.original.location ? (
						<Group spacing={4} noWrap className={styles.locationCell}>
							<IconMapPin
								size={14}
								color="#667085"
								className={styles.locationIcon}
							/>
							<div className={styles.locationText}>
								<TooltipText value={row.original.location} />
							</div>
						</Group>
					) : (
						<Text size="sm" color="dimmed">
							-
						</Text>
					)
				},
			},
			{
				accessorKey: 'listDate',
				header: 'Created Time',
				size: 105,
				maxSize: 105,
			},
			{
				accessorKey: 'updatedDate',
				header: 'Updated Time',
				size: 105,
				maxSize: 105,
			},
		],
		[handleCopyId, type],
	)

	const renderCustomActions = useCallback(
		(row: any) => {
			const original = row?.original as TutorTableRow
			const rowActionLoading = actionLoadingId === original.id
			const actionDisabled = Boolean(actionLoadingId)

			return (
				<Flex gap={6} align="center">
					<CustomTooltip label={original.isHidden ? 'Show to users' : 'Hide from users'}>
						<button
							type="button"
							onClick={() => handleHide(original)}
							disabled={actionDisabled}
							className={`${actionClasses.actionButton} ${
								original.isHidden
									? styles.purpleMutedAction
									: styles.purplePrimaryAction
							} ${rowActionLoading ? styles.actionLoading : ''}`}
						>
							{original.isHidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
						</button>
					</CustomTooltip>
					{(type === 'course' || type === 'post') && (
						<CustomTooltip label="Delete">
							<button
								type="button"
								onClick={() => handleDelete(original)}
								disabled={actionDisabled}
								className={`${actionClasses.actionButton} ${actionClasses.dangerAction} ${
									rowActionLoading ? styles.actionLoading : ''
								}`}
							>
								<IconTrash size={16} />
							</button>
						</CustomTooltip>
					)}
				</Flex>
			)
		},
		[actionLoadingId, handleDelete, handleHide, type],
	)

	const statItems = useMemo(
		() => [
			{
				label: 'Tutors',
				value: stats.tutors,
				icon: IconBriefcase,
				color: '#5A37B9',
				background: '#F4F0FF',
				border: '#D9D0FF',
			},
			{
				label: 'Learners',
				value: stats.learners,
				icon: IconUsers,
				color: '#6B3FD6',
				background: '#F6F1FF',
				border: '#DED0FF',
			},
			{
				label: 'Courses',
				value: stats.courses,
				icon: IconBook2,
				color: '#4C1D95',
				background: '#F5F3FF',
				border: '#C4B5FD',
			},
			{
				label: 'Posts',
				value: stats.posts,
				icon: IconEye,
				color: '#6941C6',
				background: '#FAF5FF',
				border: '#E9D5FF',
			},
		],
		[stats],
	)

	return (
		<Stack className={styles.page} spacing={16}>
			<FindMyTutorPanelHeader
				title={`${TYPE_LABELS[type]} Management`}
				description={TYPE_DESCRIPTIONS[type]}
				controls={
					<Flex gap={10} align="flex-end" wrap="wrap" className={styles.toolbar}>
						<Stack spacing={6} className={styles.searchControl}>
							<Text size="sm" fw={700} color="#344054">
								Search
							</Text>
							<TextInput
								value={searchKey}
								onChange={(event) => {
									setSearchKey(event.currentTarget.value)
									resetPage()
								}}
								placeholder="Search ID, name or about"
								icon={<IconSearch size={16} />}
							/>
						</Stack>

						{type === 'tutor' && (
							<Stack spacing={6} className={styles.searchControl}>
								<Text size="sm" fw={700} color="#344054">
									Country
								</Text>
								<Select
									value={countrySearch || null}
									data={resolvedCountryOptions}
									onChange={(value) => {
										setCountrySearch(value || '')
										resetPage()
									}}
									placeholder={
										countryOptionsLoading
											? 'Loading countries...'
											: 'Select tutor country'
									}
									icon={<IconMapPin size={16} />}
									searchable
									clearable
									nothingFound="No countries found"
									disabled={countryOptionsLoading}
									withinPortal
									dropdownPosition="bottom"
									zIndex={9999}
									className={styles.countrySelect}
								/>
							</Stack>
						)}

						<Stack spacing={6} className={styles.sortControl}>
							<Text size="sm" fw={700} color="#344054">
								Sort
							</Text>
							<Select
								value={sortValue}
								data={SORT_OPTIONS}
								className={styles.sortSelect}
								withinPortal
								dropdownPosition="bottom"
								zIndex={9999}
								styles={{
									input: {
										borderColor: '#c4b5fd',
										color: '#3b208e',
										fontWeight: 600,
									},
									rightSection: {
										color: '#6b3fd6',
									},
									dropdown: {
										borderColor: '#c4b5fd',
										zIndex: 9999,
									},
									item: {
										'&[data-selected]': {
											background: '#6b3fd6',
											color: '#ffffff',
										},
										'&[data-hovered]': {
											background: '#f5f3ff',
											color: '#4c1d95',
										},
									},
								}}
								onChange={(value) => {
									setSortValue(value || 'newest')
									resetPage()
								}}
							/>
						</Stack>

						<Button
							variant="default"
							leftIcon={<IconRefresh size={16} />}
							loading={loading}
							onClick={refreshPage}
							className={styles.refreshBtn}
						>
							Refresh
						</Button>
					</Flex>
				}
			/>

			<SimpleGrid cols={4} spacing="sm" breakpoints={[
				{ maxWidth: 1200, cols: 2 },
				{ maxWidth: 720, cols: 1 },
			]}>
				{statItems.map((item) => {
					const Icon = item.icon
					return (
						<Card
							key={item.label}
							withBorder
							radius="md"
							p="md"
							className={styles.statCard}
							sx={{
								borderColor: item.border,
								borderTop: `3px solid ${item.color}`,
							}}
						>
							<Flex align="center" justify="space-between" gap={12}>
								<Stack spacing={2}>
									<Text size="sm" color="dimmed">
										{item.label}
									</Text>
									<Text size={28} fw={800} color="#101828">
										{item.value}
									</Text>
								</Stack>
								<div
									className={styles.statIcon}
									style={{
										background: item.background,
										color: item.color,
									}}
								>
									<Icon size={20} />
								</div>
							</Flex>
						</Card>
					)
				})}
			</SimpleGrid>

			<div className={styles.tableWrap}>
				<TableComponent
					columns={columns}
					total={total}
					data={rows}
					pagination={pagination}
					loading={loading}
					handleChangePage={handleChangePage}
					handleChangePageSize={handleChangePageSize}
					searchQueryValue={searchKey}
					onSearchQueryInit={handleSearchQueryInit}
					filterQueryValues={{
						sort: sortValue !== 'newest' ? sortValue : undefined,
						country:
							type === 'tutor' ? countrySearch || undefined : undefined,
					}}
					onFilterQueryInit={handleFilterQueryInit}
					_renderCustomActions={renderCustomActions}
					actionColumnSize={type === 'tutor' || type === 'learner' ? 48 : 82}
					enableRowActions
					lockActionColumn
					compact
				/>
			</div>
		</Stack>
	)
}

export default TutorPage
